import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Flex } from '@abyss/web/ui/Flex';
import { Link } from '@abyss/web/ui/Link';
import { Text } from '@abyss/web/ui/Text';
import { find } from 'lodash';
import React, { useEffect, useState } from 'react';

import { adobeLinkTrackEvent } from '../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { Disposition } from '../../../../common/Constants';
import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { getFeatureFlag } from '../../../../common/Utils';
import { useSaveOfferDisposition } from '../../../../hooks/useCeOffers';
import { useCurrentMemberIsPreEffective } from '../../../../hooks/useCurrentMemberIsPreEffective';
import { useLagoon } from '../../../../hooks/useLagoon';
import { getLagoonConfigValue } from '../../../../utils/providerSearch.utils';
import {
  getCurrentMember,
  getEnterpriseId,
} from '../../../../utils/user.utils';
import { mobileOnly } from '../../../ConstantsStyles';
import { CeOffersData } from '../utils';
import { Divider } from './KeywordSearchDropdown.styled';

export const ProgramPromotionList = ({
  programPromotions,
  setSelectedOfferName,
  setIsOfferModalOpen,
  keywordSearchTerm = '',
  showDivider = true,
}) => {
  const data = useLagoon('ui-messaging')();
  const allConfigs = useLagoon('config');
  const programPromotionContent = find(data, {
    key: ConstantsLagoon.PROGRAM_PROMOTION.PROGRAM_PROMOTION_CONTENT,
  });
  const mobileScreen = useMediaQuery(mobileOnly);
  const isPreEffective = useCurrentMemberIsPreEffective();

  const enableProgramPromotionModalPopup = getLagoonConfigValue(
    allConfigs,
    'ENABLE_PROGRAM_PROMOTION_MODAL_POPUP'
  );

  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const enableAnalytics: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_PROGRAM_PROMOTION_ANALYTICS
  );

  const [hasSentDisposition, setHasSentDisposition] = useState(false);
  const [, saveOfferDisposition] = useSaveOfferDisposition({});
  const currentMember = getCurrentMember();
  const enterpriseId = getEnterpriseId(currentMember);

  useEffect(() => {
    if (hasSentDisposition) return;
    if (!enterpriseId || !programPromotions?.length) return;

    const offerTokens = programPromotions
      .map((offer) => offer?.offerToken)
      .filter(Boolean);

    if (!offerTokens.length) return;

    offerTokens.forEach((offerToken) => {
      saveOfferDisposition({
        variables: {
          enterpriseId,
          disposition: Disposition.OFFER_CONTACT,
          offerToken,
        },
      });
    });

    // Track keyword search that resulted in offer being shown
    if (enableAnalytics && keywordSearchTerm && programPromotions.length > 0) {
      programPromotions.forEach((offer) => {
        adobeLinkTrackEvent({
          name: `${offer?.offerName} | offer shown`,
          location: programPromotionContent?.title,
          userSelections: `keyword:${keywordSearchTerm}|offer:${offer?.offerName}`,
        });
      });
    }

    setHasSentDisposition(true);
  }, [
    hasSentDisposition,
    enterpriseId,
    programPromotions,
    keywordSearchTerm,
    enableAnalytics,
  ]);
  const handleOfferClick = (e: React.MouseEvent, offer: CeOffersData) => {
    e.preventDefault();

    if (isPreEffective || !offer?.cta?.ctaValue) return;

    const enterpriseId = getEnterpriseId(currentMember);

    // Track offer click in typeahead
    if (enableAnalytics) {
      adobeLinkTrackEvent({
        name: offer?.offerName,
        location: programPromotionContent?.title,
        destinationUrl: '',
        userSelections: keywordSearchTerm
          ? `keyword:${keywordSearchTerm}|offer:${offer?.offerName}`
          : '',
      });
    }

    if (enableProgramPromotionModalPopup === 'Y') {
      setSelectedOfferName(offer?.offerName);
      setIsOfferModalOpen(true);
    } else {
      if (offer?.offerToken && enterpriseId) {
        saveOfferDisposition({
          variables: {
            enterpriseId,
            disposition: Disposition.ACCEPTED,
            offerToken: offer?.offerToken,
          },
        });
      }

      // Open vendor website directly
      window.open(offer.cta.ctaValue, '_blank');
    }
  };

  return (
    <React.Fragment>
      {showDivider && <Divider />}
      <Text
        color="$gray8"
        css={{ paddingBottom: '4px' }}
        data-testid="program-promotion-section-heading"
        fontWeight={700}
        size="14px"
      >
        {programPromotionContent?.title}
      </Text>
      {programPromotions.map((offer, idx) => (
        <Flex
          align="start"
          css={{
            gap: '8px',
            '@media (max-width: 600px)': {
              gap: '8px',
            },
          }}
          direction="column"
          key={offer.offerId + idx}
        >
          {offer?.logo && (
            <div
              style={{
                width: mobileScreen ? '60px' : '96px',
                height: mobileScreen ? 'auto' : '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <img
                alt={`${offer.offerName} logo`}
                src={offer.logo}
                style={{
                  height: '25px',
                  width: 'auto',
                  maxWidth: '100%',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </div>
          )}
          <Link
            css={{
              'abyss-link-root': {
                fontSize: '16px',
                lineHeight: '20px',
                fontWeight: 600,
                textAlign: 'left',
                width: 'fit-content',
              },
            }}
            data-auto-testid={`program-promotion-${offer?.offerName}-link-text`}
            data-testid={`program-promotion-${offer?.offerName}-link-text`}
            hideIcon
            href={offer?.cta?.ctaValue || '#'}
            isDisabled={isPreEffective || !offer?.cta?.ctaValue}
            onClick={(e) => handleOfferClick(e, offer)}
          >
            {offer?.offerName}
          </Link>
          <Text
            color="$gray7"
            css={{
              maxWidth: '564px',
              lineHeight: '18px',
            }}
            data-testid={`program-promotion-${offer?.offerName}-description`}
            fontWeight={400}
            size="14px"
          >
            {offer?.offerDescription}
          </Text>
        </Flex>
      ))}
    </React.Fragment>
  );
};
